import CodeOutlined from '@ant-design/icons/CodeOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import message from 'antd/es/message';
import Space from 'antd/es/space';
import Tabs from 'antd/es/tabs';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';

import type { JSONSchemaType } from '@mai/types';

const { Text } = Typography;
const { TabPane } = Tabs;

const StyledCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;

  .ant-card-body {
    flex: 1;
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`;

const PreviewContent = styled.div`
  ${({ theme }) => `
  flex: 1;
  overflow-y: auto;
  padding: ${theme.padding}px;
`}
`;

const CodeBlock = styled.pre`
  ${({ theme }) => `
  background: ${theme.colorBgTextHover};
  border: ${theme.lineWidth}px ${theme.lineType} ${theme.colorBorderSecondary};
  border-radius: ${theme.borderRadius}px;
  padding: ${theme.padding}px;
  overflow-x: auto;
  font-size: ${theme.fontSizeSM}px;
  font-family: ${theme.fontFamilyCode};
  margin: 0;
`}
`;

const SchemaDescription = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  display: block;
  margin-bottom: ${theme.marginXS}px;
`}
`;

const RequiredTag = styled(Tag)`
  ${({ theme }) => `
  margin: 0;
  font-size: ${theme.fontSizeSM - 1}px;
`}
`;

const SchemaSection = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.margin}px;
`}
`;

const SchemaTitle = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSize}px;
  font-weight: ${theme.fontWeightStrong};
`}
`;

const PropertyDescription = styled(Text)`
  ${({ theme }) => `
  font-size: ${theme.fontSizeSM}px;
  flex: 1;
`}
`;

const PropertyRow = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingXS}px 0;
  border-bottom: ${theme.lineWidth}px ${theme.lineType} ${theme.colorBorderSecondary};
  display: flex;
  align-items: center;
  gap: ${theme.marginXS}px;

  &:last-child {
    border-bottom: none;
  }
`}
`;

const PropertyName = styled(Text)`
  ${({ theme }) => `
  font-weight: ${theme.fontWeightStrong};
  flex: 0 0 ${theme.sizeXXL * 2.5}px;
`}
`;

const TypeBadge = styled(Tag)`
  ${({ theme }) => `
  margin: 0;
  font-size: ${theme.fontSizeSM}px;
`}
`;

interface SchemaPreviewProps {
  schema: JSONSchemaType;
}

const typeColors: Record<string, string> = {
  string: 'green',
  number: 'blue',
  integer: 'cyan',
  boolean: 'purple',
  array: 'orange',
  object: 'red',
};

export const SchemaPreview: React.FC<SchemaPreviewProps> = ({ schema }) => {
  const [activeTab, setActiveTab] = useState('visual');
  const theme = useTheme();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
      void message.success('Schema copied to clipboard');
    } catch (err) {
      void message.error('Failed to copy schema');
    }
  };

  const downloadSchema = () => {
    const blob = new Blob([JSON.stringify(schema, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schema-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    void message.success('Schema downloaded');
  };

  const generateSampleData = (schema: JSONSchemaType): unknown => {
    if (!schema || !schema.type) return null;

    switch (schema.type) {
      case 'string':
        if (schema.format === 'email') return 'user@example.com';
        if (schema.format === 'date') return '2024-01-01';
        if (schema.format === 'date-time') return '2024-01-01T12:00:00Z';
        if (schema.format === 'uri') return 'https://example.com';
        if (schema.format === 'uuid')
          return '550e8400-e29b-41d4-a716-446655440000';
        return 'example string';

      case 'number':
        return schema.minimum || 42.5;

      case 'integer':
        return schema.minimum || 42;

      case 'boolean':
        return true;

      case 'array':
        if (schema.items && !Array.isArray(schema.items)) {
          return [generateSampleData(schema.items)];
        }
        return [];

      case 'object':
        if (schema.properties) {
          const sample: Record<string, unknown> = {};
          Object.entries(schema.properties).forEach(([key, propSchema]) => {
            sample[key] = generateSampleData(propSchema);
          });
          return sample;
        }
        return {};

      default:
        return null;
    }
  };

  const sampleData = generateSampleData(schema);

  return (
    <StyledCard
      title="Schema Preview"
      size="small"
      extra={
        <Space size="small">
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={copyToClipboard}
          >
            Copy
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={downloadSchema}
          >
            Download
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        tabBarStyle={{ paddingLeft: theme.padding, marginBottom: 0 }}
      >
        <TabPane
          tab={
            <>
              <EyeOutlined /> Visual
            </>
          }
          key="visual"
        >
          <PreviewContent>
            {schema.title && (
              <SchemaSection>
                <SchemaTitle strong>{schema.title}</SchemaTitle>
                {schema.description && (
                  <div>
                    <SchemaDescription type="secondary">
                      {schema.description}
                    </SchemaDescription>
                  </div>
                )}
              </SchemaSection>
            )}

            {schema.type === 'object' && schema.properties ? (
              <div>
                <SchemaDescription type="secondary">
                  PROPERTIES ({Object.keys(schema.properties).length})
                </SchemaDescription>
                {Object.entries(schema.properties).map(
                  ([name, propSchema]: [string, JSONSchemaType]) => (
                    <PropertyRow key={name}>
                      <PropertyName>{name}</PropertyName>
                      <TypeBadge
                        color={
                          typeColors[
                            typeof propSchema.type === 'string'
                              ? propSchema.type
                              : 'unknown'
                          ] || 'default'
                        }
                      >
                        {typeof propSchema.type === 'string'
                          ? propSchema.type
                          : 'unknown'}
                      </TypeBadge>
                      {schema.required?.includes(name) && (
                        <RequiredTag color="red">Required</RequiredTag>
                      )}
                      {propSchema.description && (
                        <PropertyDescription type="secondary">
                          {propSchema.description}
                        </PropertyDescription>
                      )}
                    </PropertyRow>
                  ),
                )}
              </div>
            ) : (
              <Text type="secondary">No properties defined</Text>
            )}
          </PreviewContent>
        </TabPane>

        <TabPane
          tab={
            <>
              <CodeOutlined /> JSON
            </>
          }
          key="json"
        >
          <PreviewContent>
            <CodeBlock>{JSON.stringify(schema, null, 2)}</CodeBlock>
          </PreviewContent>
        </TabPane>

        <TabPane
          tab={
            <>
              <FileTextOutlined /> Sample
            </>
          }
          key="sample"
        >
          <PreviewContent>
            <SchemaDescription type="secondary">EXAMPLE DATA</SchemaDescription>
            <CodeBlock>{JSON.stringify(sampleData, null, 2)}</CodeBlock>
          </PreviewContent>
        </TabPane>
      </Tabs>
    </StyledCard>
  );
};
